import { VimeoModels } from 'app/shared/components/vimeo-modal/vimeo-modals.model';
import { VimeoModalInfo } from 'app/shared/components/vimeo-modal/vimeo-modal.service';
import { Component, Input, Output, OnInit, EventEmitter, Type } from '@angular/core';

import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { RxComponent } from 'app/core/components/common/rx.component';
import { ClassesService } from 'app/solar/common/services/classes.service';
import { NotificationService } from 'app/core/services/notification.service';
import { SmartModalService, ModalSize } from 'app/core/services/smart-modal.service';
import { AddInstructorComponent } from 'app/solar/common/add-instructor/add-instructor.component';
import { EditInstructorComponent } from './edit-instructor/edit-instructor.component';

@Component({
    selector: 'itpx-class-instructor',
    templateUrl: './class-instructor.component.html',
    styleUrls: ['./class-instructor.component.css', '../loader.css', '../../base.css']
})
export class ClassInstructorComponent extends RxComponent {

    @Input()
    private class: any;
    @Input()
    private disabled: boolean = false;
    private instructorSelect = new Subject<InstructorSelectEvent>();
    private formSave = new Subject<any>();
    private addClick = new Subject<string>();
    private editClick = new Subject<string>();
    private refreshInstructors = new Subject<any>();

    @Output()    private classUpdate = new EventEmitter<any>();
    @Output()    private dirty = new EventEmitter<boolean>(false);
    @Output()    private proceed = new EventEmitter<any>();
    @Output()    private hasChanges = new EventEmitter<any>();
    @Output()    private openVimeoModal = new EventEmitter<VimeoModalInfo>();

    private productGroups: Observable<any[]>;
    private instructorsPerGroups: Observable<any>;

    constructor(
        private modalService: SmartModalService,
        private classService: ClassesService,
        private notificationService: NotificationService) {
        super();
    }

    ngOnInit() {

        this.productGroups = Observable.of(this.class.productGroups).shareReplay();

        let instructorAdded = this.addClick
            .flatMap(groupId => this.modalService.open(<Type<Component>>AddInstructorComponent, null, 'Add New Instructor')
                .do(a => console.log(a))
                .map(res => new InstructorSelectEvent(groupId, res.result, false))
                .neverError()
            )
            .shareReplay();

        let instructorEdited = this.editClick
            .flatMap(groupId => this.modalService.open(<Type<Component>>EditInstructorComponent, {id:this.class.id, groupId: groupId}, 'Manage Instructors', ModalSize.Lg)
                .do(a => console.log(a))
                .catchAndEmit(new InstructorsLoadEvent(groupId, null)))
            .shareReplay();
        instructorAdded
            .bindToLifecycle(this)
            .subscribe(instructor => this.notificationService.success('Class instructor added.'));

        let instructorsLoaded = Observable.merge(instructorAdded, instructorEdited, Observable.of(true))
            // Emit every product group
            .flatMap(_ => Observable.from(this.class.productGroups.map(pg => pg.id)))
            // Fetch instructor lists
            .flatMap((groupId: string) => this.classService.getAccreditedInstructors(groupId)
                .map(body => new InstructorsLoadEvent(groupId, body.data))
                .catchAndEmit(new InstructorsLoadEvent(groupId, null))
            )
            .share();

        let instructorSelected = Observable.merge(this.instructorSelect, instructorAdded.takeWhen(instructorsLoaded))
            .shareReplay();

        this.instructorsPerGroups = Observable.merge(instructorsLoaded, instructorSelected)
            .scan((total, event) => {
                if (event instanceof InstructorsLoadEvent) {
                    let savedInstructor = null;
                    let classInstructor = null;
                    let isVirtual = false;
                    if (this.class.instructors[event.groupId]) {
                        classInstructor = this.class.instructors[event.groupId].instructor;
                        isVirtual = this.class.instructors[event.groupId].virtual;
                    }

                    if (classInstructor) {
                        savedInstructor = { key: classInstructor.id, value: classInstructor.username };
                    }
                    let current = total[event.groupId] ? total[event.groupId].current : savedInstructor;
                    if (current && event.instructors.filter(entry => entry.key == current.key).length == 0) {
                        current = null;
                    }
                    total[event.groupId] = {
                        current: current,
                        virtual: isVirtual,
                        all: event.instructors
                    };
                } else if (event instanceof InstructorSelectEvent) {
                    total[event.groupId].virtual = event.virtual;
                    if (event.instructorId) {
                        total[event.groupId].current = total[event.groupId].all.find(i => i.key == event.instructorId);
                    }
                }
                return total;
            }, {})
            .startWith({})
            .shareReplay();

        let changes = instructorSelected
            .scan((total, event: InstructorSelectEvent) => {
                if (!total[event.groupId]) {
                    total[event.groupId] = {};
                }

                if (event.instructorId) {
                    total[event.groupId].instructor = event.instructorId;
                }

                total[event.groupId].virtual = event.virtual;
                return total;
            }, {})
            .startWith({})
            .shareReplay();

        Observable.merge(changes.map(it => Object.keys(it).length !== 0), this.classUpdate.mapTo(false))
            .startWith(false)
            .bindToLifecycle(this)
            .subscribe(isDirty => this.dirty.emit(isDirty));

        this.formSave
            .withLatestFrom(changes, (_, changes) => this.mapChangesToArray(changes))
            .flatMap(changes => this.classService.updateInstructors(this.class.id, changes).neverError())
            .map(body => body.data)
            .bindToLifecycle(this)
            .subscribe(classEvent => {
                this.hasChanges.emit(true);
                this.notificationService.success('Class instructors updated.');
                this.classUpdate.emit(classEvent);
                // TODO: quickfix
                setTimeout(() => {
                    this.proceed.emit(true);
                }, 1);
            });
    }

    private mapChangesToArray(changes: any): any[] {
        return Object.keys(changes)
            .map(key => ({ productGroupId: key, instructor: changes[key].instructor, virtual: changes[key].virtual }));
    }

    private onVirtualInstructorCheck(groupId: string, checked: boolean) {
        this.instructorSelect.next(new InstructorSelectEvent(groupId, null, checked));
    }

    private onInstructorSelect(groupId: string, instructorId: any) {
        this.instructorSelect.next(new InstructorSelectEvent(groupId, instructorId, false));
    }

    private onInstructorAddClick(groupId: string) {
        this.addClick.next(groupId);
    }

    private onInstructorEditClick(groupId: string) {
        this.editClick.next(groupId);
    }

    private onNeedHelpClick() {
        this.openVimeoModal.emit(VimeoModels.AddInstructors);
    }
}

export class InstructorsLoadEvent {
    constructor(public groupId: string, public instructors: any[]) { }
}

export class InstructorSelectEvent {
    constructor(public groupId: string, public instructorId: string, public virtual: boolean) { }
}



// WEBPACK FOOTER //
// ./src/app/solar/classes/components/wizard/class-instructor/class-instructor.component.ts